import _, {memoize} from 'lodash';

import {TeammateRoleByNamespaceModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {isGlobalAdmin} from '../../../libs/shared/core/src/models/helpers/accessModelHelpers';
import {
  isTeammateNamespace,
  isTeamNamespace,
} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ResourceMap} from '../../../libs/shared/core/src/store/commonAppTypes';
import {PermissionsEnum, RoleModel, SpecialRoleAliasesEnum} from '../../models/api/roleModel';

/*
 * Permissions API.
 */

export function isTeamAdmin(namespace: string, roleByNamespace: TeammateRoleByNamespaceModel) {
  return namespace.startsWith('tim:') && roleByNamespace[namespace] === SpecialRoleAliasesEnum.ADMIN;
}

export function isNamespaceAdmin(
  namespace: string,
  roleByNamespace: TeammateRoleByNamespaceModel,
  adminOverride = false,
) {
  // If an override was requested, check if it can be granted.
  const isUserGlobalAdmin = isGlobalAdmin(roleByNamespace);
  const isAdminOverride = adminOverride && isUserGlobalAdmin;
  return roleByNamespace[namespace] === SpecialRoleAliasesEnum.ADMIN || isAdminOverride;
}

export function hasPermission(
  roles: ResourceMap<RoleModel>,
  roleByNamespace: TeammateRoleByNamespaceModel,
  namespace: string,
  userNamespace: string,
  permission: PermissionsEnum,
  administeringTeammateNamespaces?: ReadonlySet<string>,
  adminOverride = false,
): boolean {
  // If this is a private namespace, some permissions are always granted.
  if (hasTeammatePermission(permission, namespace, userNamespace, administeringTeammateNamespaces)) {
    return true;
  }

  // If an override was requested, check if it can be granted.
  const isUserGlobalAdmin = isGlobalAdmin(roleByNamespace);
  const isTeam = isTeamNamespace(namespace);
  const canAdminOverride = isTeam && isUserGlobalAdmin;
  if (canAdminOverride && adminOverride) {
    return true;
  }

  // Otherwise, check if our access gives us this permission.
  return hasTeamPermission(roles, roleByNamespace, namespace, permission);
}

export function hasSharedPermission(
  roles: ResourceMap<RoleModel>,
  roleByNamespace: TeammateRoleByNamespaceModel,
  permission: PermissionsEnum,
  userNamespace: string,
) {
  return _(roleByNamespace)
    .keys()
    .some(
      (ns) => isTeamNamespace(ns) && hasPermission(roles, roleByNamespace, ns, userNamespace, permission),
    );
}

/*
 * Teammate namespace:
 * The permission framework does not extend to private conversations so you are
 * automatically granted most actions as long as you can access the conversation.
 */

const implicitPrivatePermissions = new Set([
  PermissionsEnum.READ_ANALYTICS,
  PermissionsEnum.APPLY_TAG,
  PermissionsEnum.EDIT_TAG,
  PermissionsEnum.CONVERSATION_ARCHIVE,
  PermissionsEnum.CONVERSATION_ASSIGN,
  PermissionsEnum.CONVERSATION_COMMENT,
  PermissionsEnum.CONVERSATION_MESSAGE,
  PermissionsEnum.CONVERSATION_MOVE,
  PermissionsEnum.CONVERSATION_TRASH,
]);

function hasTeammatePermission(
  permission: PermissionsEnum,
  namespace: string,
  userNamespace: string,
  administeringTeammateNamespaces?: ReadonlySet<string>,
): boolean {
  if (!isTeammateNamespace(namespace)) {
    return false;
  }

  if (permission === PermissionsEnum.EDIT_TAG && namespace !== userNamespace) {
    return false;
  }

  if (implicitPrivatePermissions.has(permission)) {
    return true;
  }

  // Delegated permissions.
  if (namespace !== userNamespace) {
    return administeringTeammateNamespaces !== undefined && administeringTeammateNamespaces.has(namespace);
  }

  return false;
}

/*
 * Team namespace.
 */

function hasTeamPermission(
  roles: ResourceMap<RoleModel>,
  roleByNamespace: TeammateRoleByNamespaceModel,
  namespace: string,
  permission: PermissionsEnum,
) {
  // Find our role in this namespace.
  const roleAlias = roleByNamespace[namespace];
  const role = (roleAlias && roles[roleAlias]) || roles[SpecialRoleAliasesEnum.GUEST];
  if (!role) {
    return false;
  }

  // Check if the role grants any of the permission prefixes.
  const prefixes = findPrefixesForPermission(permission);
  return role.permissions.some((p) => prefixes.has(p));
}

// Given a permission like conversation.update.archive, compute all role prefixes granting that permission:
// ['conversation.update.archive', 'conversation.update', 'conversation.edit', 'conversation', '*']
const findPrefixesForPermission = memoize(findPrefixesForPermissionBase);
export function findPrefixesForPermissionBase(permission: string) {
  const permissionTokens = permission.split('.');
  return new Set([...buildPrefixes(permissionTokens, ''), '*']);
}

const editPermissions = new Set(['read', 'update', 'delete', 'create']);

function buildPrefixes(permissionTokens: ReadonlyArray<string>, prefix: string): ReadonlyArray<string> {
  if (permissionTokens.length === 0) {
    return [];
  }

  // If our head token would be allowed within the "edit" permission, add "edit" to the prefixes.
  const [headToken, ...otherTokens] = permissionTokens;
  const isIncludedInEdit = editPermissions.has(headToken);
  const headTokenBranches = isIncludedInEdit ? [headToken, 'edit'] : [headToken];

  return _(headTokenBranches)
    .map((headTokenBranch) => {
      const newPrefix = (prefix && `${prefix}.${headTokenBranch}`) || headTokenBranch;
      return [newPrefix, ...buildPrefixes(otherTokens, newPrefix)];
    })
    .flatten()
    .value();
}

/*
 * Roles.
 */

export function hasRoleInNamespace(
  roleByNamespace: TeammateRoleByNamespaceModel,
  namespace: string,
  expectedRole?: RoleModel,
): boolean {
  if (expectedRole) {
    return roleByNamespace[namespace] === expectedRole.alias;
  }

  return Boolean(roleByNamespace[namespace]);
}
